import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "../lib/api";
import { toast } from "sonner";
import { Target as TargetIcon, TrendingUp, CheckCircle2, AlertCircle } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Goals() {
  const [g, setG] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [goalInput, setGoalInput] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/goals?year=${year}`).then((r) => {
    setG(r.data);
    setGoalInput(String(r.data.gci_goal || ""));
  });

  useEffect(() => { load(); }, [year]);

  const save = async () => {
    const v = parseFloat(goalInput);
    if (!v || v <= 0) return toast.error("Enter a valid goal");
    setSaving(true);
    try {
      await api.post("/goals", { year, gci_goal: v });
      toast.success(`Goal set to ${fmtMoney(v)} for ${year}`);
      load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!g) return <div className="text-sm text-[#050303]/50">Loading…</div>;

  const paceIcon = g.on_pace
    ? <CheckCircle2 size={16} className="text-green-600" />
    : <AlertCircle size={16} className="text-[#bf858e]" />;

  return (
    <div className="space-y-8" data-testid="goals-page">
      <header>
        <div className="overline">Command Center · Goals</div>
        <h1 className="font-heading text-5xl mt-2">GCI Goal Tracking</h1>
        <p className="text-sm text-[#050303]/60 mt-2">
          Set your annual Gross Commission Income goal — we&apos;ll track YTD, project deals-to-close, and flag pacing.
        </p>
      </header>

      <section className="core-card p-6 flex items-end gap-4 flex-wrap" data-testid="goal-input-panel">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs uppercase tracking-wider text-[#050303]/60">Annual GCI Goal · {year}</Label>
          <div className="relative mt-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#050303]/50">$</span>
            <Input
              type="number"
              value={goalInput}
              onChange={(e) => setGoalInput(e.target.value)}
              placeholder="e.g. 150000"
              className="pl-7"
              data-testid="goal-input"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-[#050303]/60">Year</Label>
          <Input
            type="number"
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value) || new Date().getFullYear())}
            className="w-28"
            data-testid="goal-year"
          />
        </div>
        <button onClick={save} disabled={saving}
          className="bg-[#050303] text-white pill-btn hover:bg-[#bf858e] disabled:opacity-50"
          data-testid="save-goal-btn">
          {saving ? "Saving…" : "Save goal"}
        </button>
      </section>

      <section className="core-card p-8" data-testid="progress-panel">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
          <div>
            <div className="overline">YTD Production vs Goal</div>
            <div className="font-heading text-4xl mt-1">
              {fmtMoney(g.ytd_gci)} <span className="text-[#050303]/40 text-2xl">/ {fmtMoney(g.gci_goal)}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="font-heading text-5xl text-[#bf858e]">{g.pct.toFixed(1)}%</div>
            <div className="flex items-center gap-1 text-xs text-[#050303]/70 justify-end mt-1">
              {paceIcon}
              {g.on_pace ? "On pace" : `Behind pace by ${fmtMoney(Math.abs(g.pace_delta))}`}
            </div>
          </div>
        </div>
        <div className="h-6 bg-[#e1b7c1] rounded-full overflow-hidden relative">
          <div className="h-full bg-gradient-to-r from-[#bf858e] to-[#050303] transition-all" style={{ width: `${Math.min(100, g.pct)}%` }} />
          {/* Pace marker */}
          <div className="absolute top-0 h-full w-0.5 bg-[#050303]/60" style={{ left: `${Math.min(100, (g.pace_target / g.gci_goal) * 100)}%` }} title="On-pace target" />
        </div>
        <div className="text-xs text-[#050303]/50 mt-2 flex items-center justify-between">
          <span>Pace marker · {fmtMoney(g.pace_target)}</span>
          <span>{g.closed_deals} deal{g.closed_deals === 1 ? "" : "s"} closed</span>
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="breakdown">
        <StatCard icon={<TargetIcon size={16} />} label="Remaining to goal" value={fmtMoney(g.remaining_gci)} />
        <StatCard icon={<TrendingUp size={16} />} label="Avg GCI / deal" value={fmtMoney(g.avg_gci_per_deal)} sub="Based on your closed pipeline" />
        <StatCard
          icon={<CheckCircle2 size={16} />}
          label="Deals needed"
          value={g.deals_needed}
          sub={`at ${fmtMoney(g.avg_gci_per_deal)} per deal`}
          bold
        />
      </section>
    </div>
  );
}

function StatCard({ icon, label, value, sub, bold }) {
  return (
    <div className={`core-card p-6 ${bold ? "bg-gradient-to-br from-white to-[#e1b7c1]/40" : ""}`}>
      <div className="flex items-center justify-between">
        <div className="overline">{label}</div>
        <div className="text-[#bf858e]">{icon}</div>
      </div>
      <div className="font-heading text-3xl mt-2 tracking-tight">{value}</div>
      {sub && <div className="text-xs text-[#050303]/55 mt-1">{sub}</div>}
    </div>
  );
}
